// frontend/src/components/Faq.jsx
import React from "react";

const DEFAULT_GROUPS = Object.freeze([
  { title: "General", items: [{ q: "What is this?", a: "An example FAQ." }] },
]);

export default function Faq({
  groups = [],
  title = "FAQs",
  subtitle = "",
  defaultGroup = 0,
  showSearch = true,
  // gradient CSS string like "linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%)"
  titleGradientCss,
}) {
  const safeGroups = React.useMemo(
    () => (Array.isArray(groups) && groups.length ? groups : DEFAULT_GROUPS),
    [groups]
  );

  const [active, setActive] = React.useState(() => {
    const len = safeGroups.length;
    return Math.min(Math.max(defaultGroup, 0), Math.max(0, len - 1));
  });

  React.useEffect(() => {
    const len = safeGroups.length;
    setActive((a) => Math.min(Math.max(a, 0), Math.max(0, len - 1)));
  }, [safeGroups]);

  const [query, setQuery] = React.useState("");

  const filteredItems = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    const items = safeGroups[active]?.items || [];
    if (!needle) return items;
    return items.filter(
      (it) =>
        (it.q || "").toLowerCase().includes(needle) ||
        (it.a || "").toLowerCase().includes(needle)
    );
  }, [safeGroups, active, query]);

  return (
    <section className="faq__wrap">
      <StyleTag />

      <header className="faq__header">
        <h2
          className="faq__title"
          style={
            titleGradientCss
              ? {
                  /* cross-browser gradient text */
                  backgroundImage: titleGradientCss,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent", // <-- needed for Firefox
                }
              : undefined
          }
        >
          {title}
        </h2>
        {subtitle ? <p className="faq__subtitle">{subtitle}</p> : null}
      </header>

      {showSearch && (
        <div className="faq__search">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search topic (keywords, e.g. royalty, credits)…"
            aria-label="Search questions in this topic"
          />
        </div>
      )}

      <nav className="faq__pills" aria-label="FAQ topics">
        {safeGroups.map((g, i) => (
          <button
            key={g.title || i}
            className={`faq__pill ${i === active ? "is-active" : ""}`}
            onClick={() => setActive(i)}
            type="button"
          >
            {g.title || `Section ${i + 1}`}
          </button>
        ))}
      </nav>

      <div className="faq__grid">
        {filteredItems.length === 0 ? (
          <div className="faq__empty">No matches. Try a different keyword.</div>
        ) : (
          filteredItems.map((it, idx) => <Card key={idx} q={it.q} a={it.a} />)
        )}
      </div>
    </section>
  );
}

function Card({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <article className={`faqcard ${open ? "open" : ""}`}>
      <button
        className="faqcard__q"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        type="button"
      >
        <span>{q}</span>
        <Chevron open={open} />
      </button>
      <div className="faqcard__a" style={{ maxHeight: open ? 600 : 0 }}>
        <div className="faqcard__a__inner">{a}</div>
      </div>
    </article>
  );
}

function Chevron({ open }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
      style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform .18s ease" }}>
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function StyleTag() {
  return (
    <style>{`
:root{
  --brand:#6D5EF9;
  --brandDark:#5B4EE8;
  --brandTint:#F3F0FF;
  --brandBorder:#E7E2FF;
  --hair:#E5E7EB;
  --ink:#111827;
  --muted:#6B7280;
  --shadow:0 10px 24px rgba(17,24,39,.08);

  /* Dark blue-to-purple gradient (matches site-wide theme) */
  --grad: linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%);
}

.faq__wrap{ max-width: 1000px; margin: 0 auto; }
.faq__header{ margin-bottom: 16px; text-align:left; }
.faq__title{ margin:0 0 8px 0; font-size: clamp(24px, 4vw, 32px); font-weight: 900; color: var(--ink); }
.faq__subtitle{ margin:0; color: var(--muted); }

.faq__pills{ display:flex; flex-wrap:wrap; gap:8px; margin: 0 0 16px; }
.faq__pill{
  border:1px solid var(--brandBorder);
  background: var(--brandTint);
  color: var(--brand);
  padding:8px 12px; border-radius:999px; font-weight:800; font-size:14px;
  transition: all .15s ease;
}
.faq__pill.is-active{
  background: var(--grad);
  color:#fff; border-color: transparent;
  box-shadow: 0 8px 18px rgba(109,94,249,.22);
}

.faq__search{ margin: 0 0 16px; }
.faq__search input{
  width:100%; height:44px; border-radius:12px; border:1px solid var(--hair); padding: 10px 12px; outline:none;
}
.faq__search input:focus{ border-color: var(--brand); box-shadow:0 0 0 3px var(--brandTint); }

.faq__grid{ display:grid; gap:12px; grid-template-columns: 1fr; }
@media (min-width: 880px){ .faq__grid{ grid-template-columns: 1fr 1fr; } }

.faq__empty{ grid-column: 1/-1; padding:14px; border:1px dashed var(--hair); border-radius:12px; color: var(--muted); text-align:center; }

.faqcard{ background:#fff; border:1px solid var(--hair); border-radius:14px; box-shadow: var(--shadow); overflow:hidden; }
.faqcard__q{
  width:100%; display:flex; align-items:center; justify-content:space-between;
  padding:12px 14px; background:linear-gradient(180deg, #fff, rgba(109,94,249,.03));
  border:none; outline:none; cursor:pointer; text-align:left; font-weight:800;
}
.faqcard__a{ max-height:0; overflow:hidden; transition: max-height .25s ease; }
.faqcard__a__inner{ padding: 0 14px 14px; color: var(--muted); line-height: 1.55; }
    `}</style>
  );
}